export const isFrontPageReady = (frontPage) => {
    return !!frontPage.template;
};

export const isTOCReady = (toc) => {
    return toc.template !== "" &&
        toc.pages.length > 0 &&
        !toc.pages.some((item) => item.page === "" || item.heading === "");
};

export const isOrgChartReady = (orgChart) => {
    if (!orgChart.template) return false;
    if (!orgChart.members || orgChart.members.length === 0) return false;
    if (orgChart.template === 3 || orgChart.template === 4 || orgChart.template === 5) {
        return !!orgChart.about.title &&
            !!orgChart.about.description;
    }
    if (orgChart.template === 6 || orgChart.template === 7) {
        return orgChart.members.every(m => m.whyTeamMember && m.whyTeamMember.trim() !== "");
    }
    return true;
};

export const isResumeReady = (resume) => {
    // Backward compatibility
    const blocks = resume.blocks || [resume];
    if (!blocks || blocks.length === 0) return false;

    return blocks.every(block => {
        if (!block.template) return false;
        if (!block.members || block.members.length === 0) return false;
        if (block.members.some((m) => !m.projects || m.projects.length === 0)) return false;

        if ((block.template == 1 || block.template == 3 || block.template == 5 || block.template == 6 || block.template == 7) &&
            block.members.some((m) => !m.whyTeamMember || m.whyTeamMember.trim() === "")) {
            return false;
        }

        if (block.members.some((m) =>
            m.projects.some((p) => !p.projectDetails || p.projectDetails.trim() === "" || (p.projectDetails || "").length > 200))) {
            return false;
        }

        if ((block.template === 3 || block.template === 4 || block.template === 6) && block.members.length < 2) return false;

        return true;
    });
};

export const isComparableReady = (comparable) => {
    // Backward compatibility or fallback
    const blocks = comparable.blocks || [comparable];
    if (!blocks || blocks.length === 0) return false;

    // Check if ALL blocks are valid
    return blocks.every(block => {
        const t = block.template;
        if (t === "" || t === undefined || t === null) return false;
        const projectsCount = block.projects ? block.projects.length : 0;
        if (t === 2 || t === 5 || t === 16 || t === 9 || t === 10 || t === 11 || t === 12 || t === 13) return projectsCount > 0;
        if (t === 1 || t === 4 || t === 6 || t == 7) return projectsCount >= 2;
        if (t === 3 || t === 14) return projectsCount >= 4;
        if (t === 8 || t === 15) {
            return projectsCount > 0 && !!(block.about?.title && block.about?.description);
        }
        return true;
    });
};

export const isApproachReady = (approach) => {
    // Backward compatibility
    const blocks = approach.blocks || [approach];
    if (!blocks || blocks.length === 0) return false;

    return blocks.every(block => {
        if (!block.template) return false;
        if (!block.questions || block.questions.length === 0) return false;
        return block.questions.every((question) =>
            !!question.heading && question.heading.trim() !== "" &&
            !!question.response && question.response.trim() !== ""
        );
    });
};

export const isLastPageReady = (lastPage) => {
    return !!lastPage.template;
};

export const hasAtLeastOneSectionReady = (proposal) => {
    return isFrontPageReady(proposal.frontPage) ||
        isTOCReady(proposal.toc) ||
        isOrgChartReady(proposal.orgChart) ||
        isResumeReady(proposal.resume) ||
        isComparableReady(proposal.comparable) ||
        isApproachReady(proposal.approach) ||
        isLastPageReady(proposal.lastPage);
};

export const formatedDate = (date, format = "-") => {
    if (!date) return "";
    const d = new Date(date);
    const day = String(d.getDate()).padStart(2, "0");
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const year = d.getFullYear();
    return `${month}${format}${day}${format}${year}`;
};

export const formatedDate2 = (date) => {
    if (!date) return "";

    const parsedDate = new Date(date);

    if (isNaN(parsedDate)) return "";

    return parsedDate.toLocaleDateString('en-US', {
        month: 'long',
        day: 'numeric',
        year: 'numeric'
    });
};

export const formatNumber = (value, decimals = 1) => {
    if (value === null || value === undefined) return "0";

    const num = Number(value);
    if (isNaN(num)) return "0";

    const absNum = Math.abs(num);

    const units = [
        { value: 1e12, symbol: "T" },
        { value: 1e9, symbol: "B" },
        { value: 1e6, symbol: "M" },
        { value: 1e3, symbol: "K" },
    ];

    for (const unit of units) {
        if (absNum >= unit.value) {
            const formatted = (num / unit.value).toFixed(decimals);
            return `${parseFloat(formatted)}${unit.symbol}`;
        }
    }

    return num.toString();
}

export const calculateTotalYears = (positions) => {
    if (!positions || positions.length === 0) return '0 MONTHS EXPERIENCE';

    let totalMonths = 0;
    positions.forEach(pos => {
        if (pos.startDate) {
            const start = new Date(pos.startDate);
            let end = new Date();
            if (pos.endDate && pos.endDate.toLowerCase() !== 'present' && pos.endDate.toLowerCase() !== 'current') {
                end = new Date(pos.endDate);
            }
            if (!isNaN(start.getTime()) && !isNaN(end.getTime())) {
                let months = (end.getFullYear() - start.getFullYear()) * 12 + (end.getMonth() - start.getMonth());
                if (end.getDate() < start.getDate()) {
                    months--;
                }
                totalMonths += Math.max(0, months);
            }
        }
    });

    const yrs = Math.floor(totalMonths / 12);
    const mths = totalMonths % 12;

    if (yrs > 0) {
        if (mths > 0) {
            return `${yrs}.${mths} YEARS EXPERIENCE`;
        } else {
            return `${yrs} ${yrs === 1 ? 'YEAR' : 'YEARS'} EXPERIENCE`;
        }
    } else {
        if (mths > 0) return `${mths} ${mths === 1 ? 'MONTH' : 'MONTHS'} EXPERIENCE`;
        return '0 MONTHS EXPERIENCE';
    }
};

export const formatTotalYearsSimple = (positions) => {
    if (!positions || positions.length === 0) return '0Y';

    let totalMonths = 0;
    positions.forEach(pos => {
        if (pos.startDate) {
            const start = new Date(pos.startDate);
            let end = new Date();
            if (pos.endDate && pos.endDate.toLowerCase() !== 'present' && pos.endDate.toLowerCase() !== 'current') {
                end = new Date(pos.endDate);
            }
            if (!isNaN(start.getTime()) && !isNaN(end.getTime())) {
                let months = (end.getFullYear() - start.getFullYear()) * 12 + (end.getMonth() - start.getMonth());
                if (end.getDate() < start.getDate()) {
                    months--;
                }
                totalMonths += Math.max(0, months);
            }
        }
    });

    const yrs = Math.floor(totalMonths / 12);
    const mths = totalMonths % 12;

    if (yrs > 0) {
        if (mths > 0) {
            return `${yrs}.${mths}Y`;
        } else {
            return `${yrs}Y`;
        }
    } else {
        return `${mths}M`;
    }
};

export const calculateYearsPerPosition = (pos) => {
    if (pos.calculatedYears !== undefined) return pos.calculatedYears;

    if (pos.startDate) {
        const start = new Date(pos.startDate);
        let end = new Date();
        if (pos.endDate && pos.endDate.toLowerCase() !== 'present' && pos.endDate.toLowerCase() !== 'current') {
            end = new Date(pos.endDate);
        }

        if (!isNaN(start.getTime()) && !isNaN(end.getTime())) {
            let months = (end.getFullYear() - start.getFullYear()) * 12 + (end.getMonth() - start.getMonth());

            // Adjust if the end day is before the start day in the month
            if (end.getDate() < start.getDate()) {
                months--;
            }

            months = Math.max(0, months);

            const yrs = Math.floor(months / 12);
            const mths = months % 12;

            if (yrs > 0) {
                if (mths > 0) {
                    pos.calculatedYears = `${yrs}.${mths}Y`;
                } else {
                    pos.calculatedYears = `${yrs}Y`;
                }
            } else {
                pos.calculatedYears = `${mths}M`;
            }

            return pos.calculatedYears;
        }
    }
    pos.calculatedYears = '0M';
    return pos.calculatedYears;
};